import type React from 'react';
import { FormField } from '~/components/form/FormField';

interface SwitchProps {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** An outside element that also describes this switch (its id), e.g. a notice saying why it is disabled. */
  describedBy?: string;
  testId?: string;
}

/** The one on/off switch: a native checkbox (role="switch") under a drawn track and knob. */
export function Switch({
  id,
  checked,
  onChange,
  disabled,
  ariaLabel,
  describedBy,
  testId,
}: SwitchProps) {
  return (
    <span className="relative inline-block h-6 w-11 shrink-0">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.checked)}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        data-testid={testId}
      />
      <span
        className={`absolute inset-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-900 peer-disabled:opacity-50 ${checked ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-700'}`}
        aria-hidden="true"
      />
      <span
        className={`pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        aria-hidden="true"
      />
    </span>
  );
}

interface ToggleFieldProps {
  id: string;
  /** The field label line, same as every other form field's. Omit it and the line's space is
   *  kept, so the switch still lines up beside labelled fields. */
  label?: string;
  /** What "on" means, shown beside the switch inside the field box. */
  text: React.ReactNode;
  /** One row of a list of switches: no label line (or its reserved space) above the row. */
  inline?: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helperText?: string;
  disabled?: boolean;
  describedBy?: string;
  testId?: string;
}

/**
 * A switch as a form field: framed by `FormField` like every other field, the switch and its text
 * in a `field-control-row` (the field height, no box), so it lines up beside a text input or
 * dropdown with no per-page offset.
 */
export function ToggleField({
  id,
  label,
  text,
  checked,
  onChange,
  helperText,
  inline,
  disabled,
  describedBy,
  testId,
}: ToggleFieldProps) {
  return (
    <FormField id={id} label={label} reserveLabelSpace={!label && !inline} helperText={helperText}>
      <label
        htmlFor={id}
        className={`field-control-row ${disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
      >
        <Switch
          id={id}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          describedBy={describedBy}
          testId={testId}
        />
        <span>{text}</span>
      </label>
    </FormField>
  );
}
